import React, { useEffect, useState } from "react";
import { useFavoritesStore } from "../store/favoritesStore";
import { getAllFavorites, addToFavorites, removeFromFavorites } from "../services/favoritesService";
import { useCartStore } from "../store/cartStore";
import { addToCart, getCart, removeFromCart, transformProductsToCartItems } from "../services/cartService";
import HeartIcon from "../assets/icons/HeartIcon";
import { Link } from "react-router-dom";
import { Product } from "../types";
import Loader from "../components/loader/Loader";

const FavoritesPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem("token"));
  const { items: favoriteItems, setItems: setFavorites } = useFavoritesStore();
  const { items: cartItems, addItem, updateItemQuantity, removeItem, setCart } = useCartStore();

  // reloadKey увеличивается кнопкой «Повторить»: перезапрашиваем список,
  // не перезагружая страницу целиком.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const syncFavorites = async () => {
      setIsLoading(true);
      try {
        setError(null);
        if (isLoggedIn) {
          const response = await getAllFavorites();
          setFavorites(response.result);
        } else {
          const localFavorites = JSON.parse(localStorage.getItem("favorites") || "[]");
          setFavorites(localFavorites);
        }
      } catch (err) {
        // Список с сервера не пришёл — показываем это явно и НЕ затираем
        // то, что уже лежит локально, иначе избранное выглядит пропавшим.
        setError("Не удалось загрузить избранное");
      } finally {
        setIsLoading(false);
      }
    };
    syncFavorites();
  }, [isLoggedIn, setFavorites, reloadKey]);

  const refreshCartFromServer = async () => {
    if (isLoggedIn) {
      const response = await getCart();
      setCart(transformProductsToCartItems(response.result));
    }
  };

  const handleRemoveFavorite = async (productId: number) => {
    try {
      if (isLoggedIn) {
        await removeFromFavorites(productId);
        const updatedFavorites = await getAllFavorites();
        setFavorites(updatedFavorites.result);
      } else {
        const updatedFavorites = favoriteItems.filter((item) => item.id !== productId);
        setFavorites(updatedFavorites);
        localStorage.setItem("favorites", JSON.stringify(updatedFavorites));
      }
    } catch (error) {
      setError("Не удалось удалить товар из избранного");
    }
  };

  const handleAddToCart = async (item: Product) => {
    const cartItem = cartItems.find((cart) => cart.id === item.id);

    try {
      if (isLoggedIn) {
        if (cartItem) {
          await addToCart(item.id, 1);
        } else {
          await addToCart(item.id, 1);
        }
        await refreshCartFromServer();
      } else {
        if (cartItem) {
          updateItemQuantity(item.id, 1);
        } else {
          addItem(item);
        }
      }
    } catch (error) {
      console.error("Ошибка при добавлении товара в корзину:", error);
    }
  };

  const handleDecrement = async (item: any) => {
    const cartItem = cartItems.find((cart) => cart.id === item.id);
    if (!cartItem) return;

    try {
      if (isLoggedIn) {
        if (cartItem.quantity && cartItem.quantity > 1) {
          await addToCart(item.id, -1);
        } else {
          await removeFromCart(item.id);
        }
        await refreshCartFromServer();
      } else {
        if (cartItem.quantity && cartItem.quantity > 1) {
          updateItemQuantity(item.id, -1);
        } else {
          removeItem(item.id);
        }
      }
    } catch (error) {
      console.error("Ошибка при уменьшении количества товара:", error);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="container mx-auto md:px-4 flex flex-col lg:flex-row gap-6 py-6 items-start">
      {/* Левая колонка - Избранные товары */}
      <div className="flex-1 border rounded-lg p-6 w-full">
        <h2 className="text-2xl font-bold mb-4">Избранные</h2>
        <p className="text-sm text-gray-500 mb-4">{favoriteItems.length} товаров</p>
        {error && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => setReloadKey((key) => key + 1)}
              className="text-sm font-semibold text-red-700 underline"
            >
              Повторить
            </button>
          </div>
        )}
        {favoriteItems.length === 0 ? (
          error ? null : <p>У вас нет избранных товаров</p>
        ) : (
          <ul className="space-y-6">
            {favoriteItems.map((item) => {
              const cartItem = cartItems.find((cart) => cart.id === item.id);

              return (
                <li key={item.id} className="flex flex-col md:flex-row justify-between">
                  {/* Левый блок с изображением и информацией */}
                  <Link to={`/product/${item.id}`}>
                    <div className="flex items-center gap-4">
                      <img
                        src={item.cover_image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg border"
                      />
                      <div>
                        <p className="text-sm text-gray-500 uppercase">
                          {item.category?.name || "Категория"}
                        </p>
                        <p className="font-bold text-lg mt-2">{item.name}</p>
                        <p className="text-xl text-black font-bold">{item.price} ₸</p>
                      </div>
                    </div>
                  </Link>
                  {/* Правый блок с кнопками */}
                  <div className="flex flex-row md:flex-col items-center md:items-end justify-between mt-4 md:mt-0">
                    <button
                      className="flex justify-end text-black hover:text-red-500"
                      onClick={() => handleRemoveFavorite(item.id)}
                    >
                      <HeartIcon isFavorite={true} className="w-5 h-5 md:w-4 md:h-4"/>
                    </button>
                    {cartItem ? (
                      <div className="flex items-center space-x-2 border rounded">
                        <button
                          className={`px-4 py-2 font-bold text-black border-black hover:bg-gray-100`}
                          onClick={() => handleDecrement(item)}
                        >
                          -
                        </button>
                        <span className="px-2">{cartItem.quantity}</span>
                        <button
                          className={`px-4 py-2 font-bold text-black border-black hover:bg-gray-100`}
                          onClick={() => handleAddToCart(item)}
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <button
                        className="py-2 px-2 bg-[#2C2C2C] text-white rounded hover:bg-gray-800"
                        onClick={() => handleAddToCart(item)}
                      >
                        В корзину
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Правая колонка - Заглушка */}
      <div className="w-full lg:w-1/3 border rounded-lg p-6 bg-blue-100 self-start">
        <h3 className="text-lg font-bold mb-4">Переместите все избранные в корзину и получите подарок</h3>
        <button className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800">
          Получить подарок
        </button>
      </div>
    </div>
  );
};

export default FavoritesPage;